/**
 * Advanced stitch layout (#691) — corner-aware placement, at the chisel's own
 * pitch.
 *
 * ## The pitch is not negotiable
 *
 * A stitching iron has FIXED TEETH. A row laid out at 4.3 mm cannot be punched
 * with a 4 mm iron — not approximately, not at all. So nothing here may stretch
 * or squeeze the pitch to make a run come out even; that would produce a
 * drawing no tool can execute. Every gap this module emits is exactly the
 * seam's pitch, with the single documented exception below.
 *
 * The only freedom available is WHERE THE ROW SITS: the whole row can be
 * shifted along the seam, rigidly. That is the entire lever, and it is enough.
 *
 * ## What that buys, and what it costs
 *
 * The default walker (`samplePolylineHoles`) marches from the start of the
 * working area and lets the remainder fall where it may — which means the
 * remainder lands at the END, and every corner in between lands wherever the
 * arithmetic puts it. March 4.2 mm round a box seam and a hole falls half a
 * millimetre before the corner and the next 3.7 mm after it. The corner is the
 * one place the thread has to turn and the most visible point on the piece, and
 * it is the one place the row looks wrong.
 *
 * This module pins a PIVOT HOLE on every corner (and on both ends of the
 * working area), then fills each run between pivots at exactly the pitch. Every
 * interior gap is the chisel's own; the run's leftover — which has to exist,
 * since a run is not a whole number of pitches — is split EVENLY between the
 * two gaps touching the pivots.
 *
 * That is what a leatherworker does by hand: mark the corners, then punch each
 * side as one unbroken march at the iron's pitch, letting the slack sit
 * symmetrically either side of the turn. It also means the run reads as
 * regular from both directions, which an odd gap dropped mid-run would not —
 * that breaks the rhythm at the one place the eye travels along it.
 *
 * The gap count per run is ROUNDED rather than floored, which bounds both end
 * gaps to `[0.75, 1.25]` pitches on its own: the leftover is at worst half a
 * pitch and half of that lands on each end. So the ticket's third bullet needs
 * no separate rule inside a run — there is no sliver to remove and no gap wide
 * enough to read as a missed hole. {@link MIN_GAP_FRACTION} still governs the
 * other case it names: two CORNERS closer together than half a pitch, where the
 * second pivot is dropped rather than punched on top of the first.
 *
 * Round PIVOT holes are the last piece, and are deliberately not part of the
 * row's geometry: `roundCornerHoles` only chooses what the pivot holes are
 * punched with — the row's own chisel, or a round punch, since a slanted slot
 * at a turn points across the thread's new direction. `markCornerHoles` in
 * `stitching.ts` flags them; without the pivot holes this layout creates, there
 * is nothing for it to flag.
 *
 * ## What counts as a corner is the seam's own decision
 *
 * Every part of this hangs on {@link seamCornerIndices}, and where it draws the
 * line is a judgement about the piece rather than a constant — see
 * {@link CORNER_MAX_SMOOTH_DEG}. The module is paper-free by design, like the
 * rest of the hole walker: it runs in the exporters and under vitest with no
 * scope to activate.
 */
import { isFeatureEnabled } from "../featureFlags";
import {
  buildSeamSegments,
  seamPlacementAt,
  type SeamHandle,
  type SeamSegment,
} from "./seamGeometry";
import type { StitchLine, Vec2 } from "./Project";

/** Whether the Advanced stitch control section is available at all (#691).
 *  On for everyone for now; it may become a paid tier later. Off ⇒ a seam
 *  carrying `cornerLayout` lays out the ordinary way, so a project made with
 *  the feature on still opens (with a plain row) where it is off. */
export function advancedStitchLayoutEnabled(): boolean {
  return isFeatureEnabled("advanced-stitch-control");
}

/** The layout fields a NEW seam starts with (#1203). Even spacing is the
 *  default wherever it's available: a seam drawn by someone who has the
 *  Advanced stitch control section starts with it on, and they turn it off
 *  for an exact-pitch row. Where the feature is off (or its preview hidden)
 *  nothing is stored, so the seam is a plain row that stays plain if the
 *  feature is turned on later. Existing seams keep what they have. */
export function newSeamLayoutDefaults(): Pick<StitchLine, "cornerLayout"> {
  return advancedStitchLayoutEnabled() ? { cornerLayout: true } : {};
}

/** How sharp a bend has to be before it counts as a corner, as the INTERIOR
 *  angle at the anchor: 180° is dead straight, 90° is square. An anchor is a
 *  corner when its interior angle is BELOW the setting, so the setting itself
 *  is the widest angle still treated as a smooth bend.
 *
 *  It has to be adjustable per seam. Too wide and every anchor of a polyline
 *  standing in for a curve becomes a pin, leaving a smooth arc studded with
 *  holes and evenly spaced nowhere; too narrow and a real but gentle corner —
 *  a chamfer, a shallow gusset fold — is walked straight past. Where the line
 *  falls is a judgement about the piece, not a constant.
 *
 *  135° as the default draws the line at an octagon: a box (90°), a pentagon
 *  (108°) and a hexagon (120°) all earn pivot holes, while an octagon (135°)
 *  sits exactly ON the setting and so stays smooth, as does anything rounder. */
export const CORNER_MAX_SMOOTH_DEG = 135;

/** The adjustable range. Above 180° means nothing — a straight line is the
 *  widest angle there is. The bottom is 0°, which pins nothing at all: a
 *  usable "no pivot holes on this seam" position, reached by dragging rather
 *  than by hunting for a separate switch. */
export const CORNER_ANGLE_MIN_DEG = 0;
export const CORNER_ANGLE_MAX_DEG = 180;

/** The shortest gap worth punching, as a fraction of the pitch. Two uses, one
 *  meaning — "automatically remove too-short of the spacing":
 *
 *   - a run's leftover ODD gap shorter than this drops a hole, so the gap
 *     becomes leftover + pitch rather than a sliver;
 *   - a CORNER pin closer than this to the pin before it is dropped, since
 *     pinning both would punch two holes almost on top of each other.
 *
 *  0.66 balances the two ways this can look wrong. Too low and a pair of holes
 *  reads as a double-punch. Too high and DROPPING one costs more than keeping
 *  it: the gap left behind is the leftover plus a whole pitch, so a floor of
 *  0.75 can leave 1.75 pitches of empty seam where 0.66 leaves at most 1.66 —
 *  and a gap that wide reads as a missed hole, which is the more visible fault
 *  of the two.
 *
 *  It also stays below the 0.75 that the gap-count rounding already guarantees
 *  for a run's adjusted ends, so at the default the every-gap floor never fires
 *  and the layout is left exactly as the rounding laid it. Raising the floor is
 *  a deliberate act; the default is not quietly reshaping anything.
 *
 *  The DEFAULT only: a seam can set its own via
 *  {@link effectiveMinGapFraction}, because how tight is too tight depends on
 *  the leather and the thread as much as on the iron. */
export const MIN_GAP_FRACTION = 0.66;

/** The shortest gap this seam will punch, as a fraction of its pitch (#691).
 *  Clamped to 0…1: 1 means "nothing shorter than a full stitch", and 0 turns
 *  the rule off, so every leftover is punched however tight. */
export function effectiveMinGapFraction(line: StitchLine): number {
  const v = line.minGapFraction;
  if (v == null || !Number.isFinite(v)) return MIN_GAP_FRACTION;
  return Math.max(0, Math.min(1, v));
}

export interface LayoutGeometry {
  points: Vec2[];
  handles?: SeamHandle[];
  closed: boolean;
  /** Arc-length positions (mm from the first anchor) where a hole must be
   *  pinned: the middle of each rounded corner's arc, or the vertex itself when
   *  the corner is square. Ascending. */
  cornerAt: number[];
  /** EVERY anchor that has an angle, by arc position, with that angle. A
   *  superset of `cornerAt`: when a run holds no corner at all, its leftover
   *  still has to go somewhere, and the least-straight bend in it is where a
   *  leatherworker would put it. */
  bendAt: Array<{ at: number; interiorDeg: number }>;
  segs: SeamSegment[];
  total: number;
}

/** The corner-angle threshold this seam judges by, defaulted and clamped to the
 *  adjustable range so a stored value from an older build (or a hand-edited
 *  file) can't switch corner detection off entirely. */
export function effectiveCornerAngle(line: StitchLine): number {
  const v = line.cornerMaxSmoothAngle;
  if (v == null || !Number.isFinite(v)) return CORNER_MAX_SMOOTH_DEG;
  return Math.max(CORNER_ANGLE_MIN_DEG, Math.min(CORNER_ANGLE_MAX_DEG, v));
}

/**
 * Whether this seam wants the corner-aware layout — the per-seam opt-in AND the
 * feature flag.
 *
 * This is what creates PIVOT HOLES: a hole exactly on each corner, with the
 * runs between them — and between the end pivot holes and the ends of the seam
 * — spaced out evenly. Whether those pivot holes are punched with the row's own
 * chisel or forced round is a separate question, `roundCornerHoles`, which is
 * meaningless without them.
 *
 * Never on a construction-grid FOLLOWER (it carries a derived `gridPhase`,
 * #1105): its holes are the leader's, so a layout of its own could only move
 * them off their partners. A `cornerLayout` it stored before it joined the
 * group is kept, not cleared — it applies again if the seam leaves the group.
 */
export function usesCornerLayout(line: StitchLine): boolean {
  return (
    line.cornerLayout === true &&
    line.gridPhase == null &&
    advancedStitchLayoutEnabled()
  );
}

function handleAt(handles: SeamHandle[] | undefined, i: number): SeamHandle {
  const e = handles?.[i];
  return { in: e?.in ?? { x: 0, y: 0 }, out: e?.out ?? { x: 0, y: 0 } };
}

function isZero(v: Vec2): boolean {
  return Math.abs(v.x) < 1e-9 && Math.abs(v.y) < 1e-9;
}

function unit(v: Vec2): Vec2 | null {
  const len = Math.hypot(v.x, v.y);
  return len < 1e-9 ? null : { x: v.x / len, y: v.y / len };
}

/**
 * The anchors of `points` that are CORNERS — those whose INTERIOR angle is
 * below `maxSmoothDeg` (180° = straight, 90° = square). An angle exactly ON the
 * setting counts as smooth, so the setting reads as "the widest angle I still
 * want treated as a bend".
 *
 * Handles are honoured, so an anchor between two curved runs is judged on its
 * tangents (a smooth join is not a corner however far the curve travels either
 * side of it).
 *
 * An open seam's first and last anchors are never corners: nothing arrives at
 * one and nothing leaves the other, and both are already pinned by the working
 * area.
 */
export function seamCornerIndices(
  points: Vec2[],
  closed: boolean,
  handles?: SeamHandle[],
  maxSmoothDeg: number = CORNER_MAX_SMOOTH_DEG,
): number[] {
  // The epsilon makes "exactly on the setting" reliably SMOOTH rather than a
  // coin toss. A regular hexagon at a 120° setting sits precisely on the line,
  // and its six angles — each built from chords through `Math.cos`/`Math.sin` —
  // land a few ulps either side of it, so without this some vertices come out
  // cornered and some don't. 1e-6 of a degree is far below any angle a user can
  // mean and far above the noise.
  return seamBendAngles(points, closed, handles)
    .filter((b) => b.interiorDeg < maxSmoothDeg - 1e-6)
    .map((b) => b.index);
}

/**
 * Every anchor that HAS an angle, with that angle in degrees — 180 is dead
 * straight, 90 square. Corner detection is a threshold over this
 * ({@link seamCornerIndices}), and so is the search for the least-straight bend
 * when a run has no corner at all but its leftover still has to go somewhere.
 * One definition of "the angle here", used by both.
 *
 * A BEZIER anchor — one carrying either handle — is skipped outright, however
 * sharply it turns. That is the escape hatch: pulling handles out of a point is
 * how the maker says "the stitching runs THROUGH here", and honouring that is
 * more useful than any angle test, because the alternative is a threshold fight
 * over how sharp a curve's join has to be before it counts. A plain anchor is
 * judged on its chords; if you want one to stop being a pivot, give it handles.
 *
 * An open seam's first and last anchors are skipped too: nothing arrives at one
 * and nothing leaves the other.
 */
export function seamBendAngles(
  points: Vec2[],
  closed: boolean,
  handles?: SeamHandle[],
): Array<{ index: number; interiorDeg: number }> {
  const n = points.length;
  if (n < 3) return [];
  const out: Array<{ index: number; interiorDeg: number }> = [];
  for (let i = 0; i < n; i++) {
    if (!closed && (i === 0 || i === n - 1)) continue;
    const prev = points[(i - 1 + n) % n]!;
    const next = points[(i + 1) % n]!;
    const here = points[i]!;
    const hi = handleAt(handles, i);
    // The escape hatch: a point with handles is the maker saying the row runs
    // through it, so it never becomes a pivot however sharp the tangents are.
    if (!isZero(hi.in) || !isZero(hi.out)) continue;
    const inVec = { x: here.x - prev.x, y: here.y - prev.y };
    const outVec = { x: next.x - here.x, y: next.y - here.y };
    const a = unit(inVec);
    const b = unit(outVec);
    if (!a || !b) continue;
    // The dot is cos(TURN); interior = 180 − turn.
    const dot = Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y));
    const turnDeg = (Math.acos(dot) * 180) / Math.PI;
    out.push({ index: i, interiorDeg: 180 - turnDeg });
  }
  return out;
}

/**
 * The geometry the holes are laid on, plus where its corners are in arc length.
 *
 * The seam itself, always: this module changes where holes GO, never where the
 * seam runs. Corner positions are its sharp vertices, matched by anchor
 * IDENTITY rather than index, because `buildSeamSegments` drops zero-length
 * runs and a positional lookup would slide off by one on a seam with a
 * duplicated point.
 */
export function stitchLayoutGeometry(
  line: StitchLine,
  closed: boolean,
): LayoutGeometry {
  const bends = seamBendAngles(line.points, closed, line.handles);
  const maxSmooth = effectiveCornerAngle(line);
  const { segs, total } = buildSeamSegments(line.points, closed, line.handles);
  // Anchor -> arc position, matched by IDENTITY rather than index because
  // `buildSeamSegments` drops zero-length runs and a positional lookup would
  // slide off by one on a seam with a duplicated point.
  const startOf = new Map<Vec2, number>();
  for (const seg of segs)
    if (!startOf.has(seg.from)) startOf.set(seg.from, seg.cumStart);
  const bendAt: Array<{ at: number; interiorDeg: number }> = [];
  for (const b of bends) {
    const at = startOf.get(line.points[b.index]!);
    if (at != null) bendAt.push({ at, interiorDeg: b.interiorDeg });
  }
  bendAt.sort((a, b) => a.at - b.at);
  return {
    points: line.points,
    handles: line.handles,
    closed,
    cornerAt: bendAt
      .filter((b) => b.interiorDeg < maxSmooth - 1e-6)
      .map((b) => b.at),
    bendAt,
    segs,
    total,
  };
}

/**
 * Hole positions for one run between two pins, at the EXACT pitch.
 *
 * Both pins carry a hole (the caller emits those). Every INTERIOR gap is
 * exactly `pitch` — a stitching iron has fixed teeth, so a row spaced at
 * anything else is unpunchable — and the run's leftover, which has to exist
 * since a run is not a whole number of pitches, is split EVENLY between the two
 * END gaps, the ones touching the pins.
 *
 * That is where a leatherworker puts it. The run reads as one unbroken march at
 * the chisel's pitch, and the slack sits symmetrically either side of the turn,
 * so a corner still looks deliberate from both directions. Putting the leftover
 * mid-run instead — one odd gap in the middle of an otherwise exact row — is
 * more visible, not less: it breaks the rhythm at the one place the eye is
 * travelling along it.
 *
 * The gap count is ROUNDED rather than floored, which bounds both end gaps to
 * `[0.75, 1.25]` pitches without any special-casing: the leftover is at worst
 * half a pitch, and half of that lands on each end. So there is no sliver to
 * remove here and no gap wide enough to read as a missed hole.
 *
 * Returns the INTERIOR positions only, so a pin shared by two runs is never
 * punched twice. Ascending.
 */
export function exactPitchSpanPositions(
  from: number,
  to: number,
  pitch: number,
  /** Whether each END of the run may absorb the leftover. A PIVOT hole can —
   *  widening the gap beside a corner is what keeps the corner symmetric. The
   *  seam's own start / end cannot when the maker has asked for the row to
   *  begin and end exactly on pitch (#691); there the slack goes entirely to
   *  the pivot side, and with no pivot at either end there is nowhere to put it
   *  and the run is simply marched at the pitch. */
  adjustFrom = true,
  adjustTo = true,
  /** Where to park the odd gap when NEITHER end may take it — the arc position
   *  of the least-straight bend in the run, or its midpoint when the run is
   *  dead straight. The gap lands on whichever of the exact-pitch slots sits
   *  closest to this. */
  slackAt?: number,
  /** The seam's shortest punchable gap, as a fraction of the pitch. */
  minGapFraction: number = MIN_GAP_FRACTION,
): number[] {
  const span = to - from;
  if (!(span > 0) || !(pitch > 0)) return [];

  // Neither end can take it. The row is an exact-pitch march either side of ONE
  // odd gap, and the only question is where that gap goes. Dumping it against
  // the far pin — which is what marching straight through does — puts it at the
  // end of the stitching, the one place the maker was promised it wouldn't be.
  if (!adjustFrom && !adjustTo) {
    let full = Math.floor(span / pitch + 1e-9);
    let odd = span - full * pitch;
    // A sliver reads as a double-punch, so give it a whole pitch to live in.
    if (full >= 1 && odd > 1e-9 && odd < pitch * minGapFraction) {
      full -= 1;
      odd += pitch;
    }
    if (full < 1) return [];
    if (odd <= 1e-9) {
      // Divides exactly: no odd gap to place. Stop one short of `full` — the
      // hole at `from + full * pitch` IS the far pin, which the caller emits,
      // and returning it here punched two holes on the same spot.
      const out: number[] = [];
      for (let i = 1; i < full; i++) out.push(from + i * pitch);
      return out;
    }
    // `full` exact gaps plus the odd one ⇒ `full` interior holes. Choose which
    // slot the odd gap occupies by whose MIDPOINT lands nearest `slackAt`.
    const target = slackAt ?? from + span / 2;
    let best = 0;
    let bestD = Infinity;
    for (let j = 0; j <= full; j++) {
      const mid = from + j * pitch + odd / 2;
      const d = Math.abs(mid - target);
      if (d < bestD) {
        bestD = d;
        best = j;
      }
    }
    const out: number[] = [];
    for (let i = 1; i <= best; i++) out.push(from + i * pitch);
    for (let i = best; i < full; i++) out.push(from + i * pitch + odd);
    return out;
  }

  // Gaps the run is closest to holding. Rounding (not flooring) is what keeps
  // the adjusted gaps near the pitch instead of letting a whole leftover pitch
  // pile onto them.
  let gaps = Math.round(span / pitch);
  // One gap or fewer: the run is shorter than about one and a half stitches, so
  // the two pins are the whole row and there is nothing to put between them.
  if (gaps < 2) return [];
  const floor = pitch * minGapFraction;
  // The adjusted gap this run would carry — both ends share the leftover when
  // both may take it, otherwise the single free end takes all of it.
  const adjusted = (n: number) =>
    adjustFrom && adjustTo
      ? (span - (n - 2) * pitch) / 2
      : span - (n - 1) * pitch;
  // Honour the seam's floor: a run holding one fewer gap gives every adjusted
  // gap more room, so drop holes until nothing is tighter than the maker asked
  // for. Rounding already keeps these within a quarter of the pitch, so at the
  // default this loop never runs — it earns its keep when the floor is raised
  // toward a full stitch, which is exactly when "shortest stitch spacing" is
  // supposed to mean something.
  while (gaps > 2 && adjusted(gaps) < floor - 1e-9) gaps -= 1;
  const out: number[] = [];

  if (adjustFrom && adjustTo) {
    // `gaps - 2` interior gaps at exactly the pitch, and what's left over
    // shared equally by the two end gaps.
    const end = adjusted(gaps);
    for (let i = 0; i <= gaps - 2; i++) out.push(from + end + i * pitch);
    return out;
  }
  // One end fixed: march at the exact pitch AWAY from it, so every gap but the
  // one touching the pivot is the chisel's own.
  if (adjustTo) {
    for (let i = 1; i <= gaps - 1; i++) out.push(from + i * pitch);
  } else {
    for (let i = gaps - 1; i >= 1; i--) out.push(to - i * pitch);
  }
  return out;
}

/**
 * Corner-aware hole placement (#691).
 *
 * `window` is the absolute arc-length working area for an open seam (its two
 * ends become pins); a closed loop stitched as a continuous ring passes `null`
 * and its pins are its corners alone, with the final span wrapping through the
 * start.
 *
 * Every gap is the EXACT pitch bar one per run — see
 * {@link exactPitchSpanPositions} for why that one exists and why it sits
 * mid-run. Nothing here rescales a gap: a stitching iron has fixed teeth.
 *
 * A corner pin closer than {@link MIN_GAP_FRACTION} × pitch to the pin before it
 * is dropped: a 2 mm bevel between two corners gets one hole, not two touching
 * ones. Working-area ends are never dropped — they are the seam, not a feature
 * of it.
 */
export function samplePolylineHolesEvenCorners(
  line: StitchLine,
  closed: boolean,
  pitch: number,
  window: { start: number; end: number } | null,
): Array<{ position: Vec2; tangentDeg: number }> {
  const geo = stitchLayoutGeometry(line, closed);
  const { segs, total, cornerAt, bendAt } = geo;
  if (segs.length === 0 || total <= 0 || pitch <= 0) return [];
  const at = (d: number) =>
    seamPlacementAt(segs, closed ? modPos(d, total) : Math.min(total, d));
  const minGap = pitch * effectiveMinGapFraction(line);

  // The pin list, in walking order. An open seam runs from its window start to
  // its window end and pins both; a ring starts at its first corner and comes
  // back round to it.
  let pins: number[];
  let ringWrap = false;
  // Which pins are PIVOT holes (corners) as opposed to the seam's own ends.
  // Only a pivot may absorb a run's leftover once the maker has asked for the
  // row to start and finish exactly on pitch.
  let pivotAt: Set<number> | null = null;
  if (window) {
    const lo = closed
      ? window.start
      : Math.max(0, Math.min(total, window.start));
    const hi = closed ? window.end : Math.max(lo, Math.min(total, window.end));
    if (hi <= lo + 1e-9) return [at(lo)];
    // A closed loop's window is measured from the start-of-path origin and may
    // wrap, so corners are compared in the window's own frame.
    const inWindow = cornerAt
      .flatMap((c) => (closed ? [c, c + total, c - total] : [c]))
      .filter((c) => c > lo + 1e-9 && c < hi - 1e-9)
      .sort((a, b) => a - b);
    pins = [lo, ...inWindow, hi];
    // The window's own two ends are not pivots; every corner between them is.
    pivotAt = new Set(inWindow);
  } else {
    if (cornerAt.length === 0) {
      // A corner-free ring — a circle seam. Nothing to pin, so the only lever
      // is where the row STARTS, and the user already owns that: start-of-path.
      // March from it at the exact pitch all the way round; the circumference's
      // leftover lands in the closing gap, immediately BEFORE the start point.
      //
      // The sliver doesn't vanish — it can't, without stretching the pitch,
      // which no iron can punch. What it gets is a place the user chose and a
      // floor: too short to punch and the last hole is dropped, so the closing
      // gap lands in `[MIN_GAP_FRACTION, 1 + MIN_GAP_FRACTION]` pitches instead
      // of being whatever was left over.
      // N holes at an exact pitch around a ring of length L leave N - 1 gaps of
      // `pitch` and one closing gap of `L - (N - 1) * pitch`. Pick the largest
      // N whose closing gap still clears the floor — one expression, no
      // special case for "the leftover was too short", which is where an
      // earlier version of this managed to drop a hole it had already not
      // counted and leave a gap of two and a half pitches.
      const holes = Math.max(1, Math.floor((total - minGap) / pitch) + 1);
      const off = line.startOffset ?? 0;
      const out: Array<{ position: Vec2; tangentDeg: number }> = [];
      for (let i = 0; i < holes; i++) out.push(at(off + i * pitch));
      return out;
    }
    pins = [...cornerAt];
    ringWrap = true;
    // A ring has no ends, so every pin is a pivot and the option cannot apply.
    pivotAt = new Set(cornerAt);
  }

  // Drop pins that crowd the one before them. On a ring the FIRST pin is the
  // anchor everything else is measured from, so the walk starts at index 1 and
  // the wrap gap is checked at the end.
  const kept: number[] = [pins[0]!];
  for (let i = 1; i < pins.length; i++) {
    const p = pins[i]!;
    const isLast = i === pins.length - 1;
    // The window END is a hard pin: dropping it would leave the row stopping
    // short of the working area. Drop the CORNER before it instead.
    if (!ringWrap && isLast) {
      while (kept.length > 1 && p - kept[kept.length - 1]! < minGap) kept.pop();
      kept.push(p);
      continue;
    }
    if (p - kept[kept.length - 1]! < minGap) continue;
    kept.push(p);
  }
  if (ringWrap && kept.length > 1) {
    // Closing gap on a ring: if the last corner crowds the first, drop it.
    const wrapGap = kept[0]! + total - kept[kept.length - 1]!;
    if (wrapGap < minGap) kept.pop();
  }

  const out: Array<{ position: Vec2; tangentDeg: number }> = [];
  for (let i = 0; i < kept.length; i++) {
    const a = kept[i]!;
    out.push(at(a));
    const next =
      i + 1 < kept.length ? kept[i + 1]! : ringWrap ? kept[0]! + total : null;
    if (next == null) break;
    // With "don't add space at the ends" on, only a pivot end may take the
    // leftover; the seam's own start / end keeps the exact pitch beside it.
    const fixEnds = line.noEndSpacing !== false && !ringWrap;
    const adjustFrom = !fixEnds || (pivotAt?.has(a) ?? true);
    const adjustTo =
      !fixEnds || ringWrap || (pivotAt?.has(kept[i + 1]!) ?? true);
    // With neither end free to take the leftover, it has to sit somewhere
    // inside the run. Put it at the least-straight bend in there — the closest
    // thing to a corner the run has, even though it didn't qualify for a pivot
    // hole — and at the run's midpoint when the run is dead straight. Anywhere
    // is better than against the far pin, which is the end of the stitching.
    let slackAt: number | undefined;
    if (!adjustFrom && !adjustTo) {
      let sharpest: { at: number; interiorDeg: number } | null = null;
      for (const b of bendAt) {
        if (b.at <= a + 1e-9 || b.at >= next - 1e-9) continue;
        if (!sharpest || b.interiorDeg < sharpest.interiorDeg) sharpest = b;
      }
      slackAt = sharpest ? sharpest.at : a + (next - a) / 2;
    }
    for (const d of exactPitchSpanPositions(
      a,
      next,
      pitch,
      adjustFrom,
      adjustTo,
      slackAt,
      effectiveMinGapFraction(line),
    ))
      out.push(at(d));
  }
  return out;
}

function modPos(d: number, total: number): number {
  return total > 0 ? ((d % total) + total) % total : 0;
}
